import { useNavigate } from 'react-router-dom';
import { fetchOrderActiveTicket } from '@/api';
import { formByEntityFieldId, TicketEntityIDFieldEnum } from '@/constants';
import { useFetchTicketFormFields } from '@/hooksApi';
import { useTicketingStore } from '@/stores';
import useLanguageStore from '@/stores/useLanguageStore';
import usePermission from './usePermission';
import { PermissionName } from '@/types';

export const useOpenEntityTicketModal = (entity: TicketEntityIDFieldEnum) => {
  const { openModal, setTicketFormData } = useTicketingStore();
  const { language } = useLanguageStore();
  const navigate = useNavigate();

  const { hasPermission } = usePermission();

  const { data: formFieldList } = useFetchTicketFormFields(
    language,
    hasPermission(PermissionName.CREATE_TICKET),
  );

  const formId = formByEntityFieldId[entity].formId;

  const platformCodeField = formFieldList?.object
    .find((form) => form.id === formId)
    ?.fields.find((field) => field.id === entity);

  /**
   * PRD R1/R2/R3 — one ticket per ORDER, whatever its status.
   *
   * 🔴 RAISING A TICKET ON AN ORDER THAT ALREADY HAD ONE CREATED A SECOND,
   * splitting the conversation across two threads and leaving the agent
   * chasing context. The check now runs first and matches any status except
   * deleted, so a Resolved or Closed ticket is CONTINUED — the merchant lands
   * on its history and their message becomes a comment that reopens it.
   *
   * ⚠️ ORDERS ONLY. The PRD scopes this to "a ticket on an order" and its
   * non-goals keep the one-ticket-per-order relationship as the assumption.
   * Pickups and remittances are unchanged, deliberately — extending it there
   * would be inventing a rule nobody asked for.
   */
  const continueExistingOrderTicket = async (entityUid?: string) => {
    if (entity !== TicketEntityIDFieldEnum.ORDER || !entityUid) return false;

    try {
      const res = await fetchOrderActiveTicket(entityUid);
      const envelope = res?.object;
      const ticketId = envelope?.ticket?.id;

      // ⭐ NEEDS ALL THREE: the lookup must have SUCCEEDED, it must have found
      // something, and that something must be addressable. A ticket we cannot
      // open is not a ticket we can continue.
      if (envelope?.lookup_ok && envelope?.has_active_ticket && ticketId) {
        navigate(`/ticket-center/details/${ticketId}`);
        return true;
      }
    } catch {
      // Deliberately swallowed — see below.
    }

    // 🔴 FALLS THROUGH TO THE CREATION FORM ON ANY UNCERTAINTY, AND THAT IS A
    // CORRECTION OF MY OWN EARLIER REASONING. I first made the app BLOCK here,
    // arguing that the lookup and creation share a service so a broken check
    // means creation would fail anyway. `activeTicket` then turned out to have
    // returned `lookup_ok:false` on every call since it shipped, while
    // creation worked fine — so blocking would have denied every merchant a
    // support ticket entirely.
    //
    // A duplicate thread is a nuisance, recoverable by an agent, and is
    // today's behaviour. No route to support is none of those. "Fail closed"
    // is only conservative when you know what closed costs.
    return false;
  };

  const openEntityTicketModal = async (
    platformCode?: string,
    entityUid?: string,
    entityFieldLabelKey?: string,
  ) => {
    if (await continueExistingOrderTicket(entityUid)) return;

    if (platformCodeField?.id) {
      setTicketFormData({
        ticket_form_id: formId,
        external_id: platformCode,
        entity_uid: entityUid,
        entity_field_label_key: entityFieldLabelKey,
        custom_fields: [
          {
            id: platformCodeField?.id,
            value: platformCode ?? '',
          },
        ],
      });
    }
    openModal();
  };

  return {
    openEntityTicketModal,
  };
};
